import React, { useContext, useEffect, useRef, useState } from 'react';

import { FaInfoCircle } from 'react-icons/fa';
import styled, { css } from 'styled-components';

import { RadioButton, RadioButtonGroup } from '@techstyle/react-components';
import { FormattedMessage } from '@techstyle/react-intl';

import { Component as Price } from '../ExtendedPrice';
import { useProductDetailContext } from '../ProductDetailLayout/ProductDetailContext';
import ToastContext from '../ToastContext/ToastContext';

import FeaturedItemsList from './FeaturedItemsList';

type PriceChooserProps = {
  isMember?: boolean;
  memberPrice: number;
  guestPrice: number;
  promoPrice: number;
  renderPromoHeader: React.ReactNode;
  showPromoText: boolean;
  shouldGreyedOut: boolean;
};

const inputStyle = css`
  width: 20px;
  height: 20px;
  font-size: 10px;
  margin: 0px;
  position: relative;
  background-color: transparent;
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 80%;
    height: 80%;
    border-radius: 50%;
    background-color: transparent;
    transition: background-color 0.8s ease-out;
  }
`;

const inputCheckedStyle = css`
  background-color: transparent;
  &::after {
    background-color: ${props => props.theme.colors.default};
  }
`;

const RadioButtonWrapper = styled.label<{
  isSelected: boolean;
  isMember: boolean;
}>`
  display: flex;
  flex-direction: column;
  padding: 10px 8px
    ${({ isSelected, isMember }) => (isSelected && isMember ? '0px' : '10px')}
    8px;
  border: 2px solid transparent;
  transition: border-color ${props => (props.isSelected ? '1.5s' : '0.5s')}
      ease-out,
    background-color ${props => (props.isSelected ? '0.6s' : '0.4s')} ease-out;
  ${({ isSelected, isMember, theme }) =>
    isSelected &&
    `
    border-color: ${theme.colors.default};
    ${
      isMember &&
      `
      background-color: ${theme.colors.lavender200};
      border-color: ${theme.colors.lavender};
    `
    }
  `}
`;

const RadioButtonContentWrapper = styled.div<{
  isMember?: boolean;
  showPromoText?: boolean;
}>`
  display: flex;
  flex-direction: row;
  align-items: ${props =>
    props.isMember && props.showPromoText ? 'flex-start' : 'center'};
  padding-top: ${props =>
    props.isMember && props.showPromoText ? '4px' : '0'};
`;

const RadioButtonContent = styled.div<{ isMember: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  flex-grow: 1;
  margin-left: 10px;
`;

interface StyledRadioButtonProps {
  isMember?: boolean;
}

const inputFocusStyle = css`
  box-shadow: 0 0 0 2px ${props => props.theme.colors.default}33;
`;

const StyledRadioButton = styled(RadioButton).attrs(() => ({
  inputCheckedStyle: inputCheckedStyle,
  inputStyle: inputStyle,
  inputFocusStyle: inputFocusStyle,
}))<StyledRadioButtonProps>`
  align-self: ${props => (props.isMember ? 'flex-start' : 'center')};
  margin-top: ${props => (props.isMember ? '4px' : '0')};
`;

const MemberHeader = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  text-wrap: balance;
`;

const PriceLabel = styled.span<{ shouldGreyedOut?: boolean }>`
  color: ${({ theme }) => theme.colors.black};
  padding: 4px 0;
  text-transform: uppercase;
  margin-left: auto;
  ${({ shouldGreyedOut }) =>
    shouldGreyedOut &&
    `
    color: gray;
    text-decoration: line-through;
  `}
`;

const PriceWrapper = styled.div<{ shouldGreyedOut?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  ${({ shouldGreyedOut }) =>
    shouldGreyedOut &&
    `
  color: gray;

`}
`;

const PriceRadioGroup = styled(RadioButtonGroup).attrs({
  name: 'confirm-price',
  verticalAlign: 'center',
})`
  grid-gap: 0;
`;

const ErrorTransparentBox = styled.div`
  color: ${props => props.theme.colors.error};
  width: 100%;
  padding: 8px 7px;
  font-size: 12px;
  text-align: left;
  text-transform: none;
  display: flex;
  align-items: center;
`;
const InfoCircleIcon = styled(FaInfoCircle)`
  font-size: 20px;
  margin-right: 20px;
`;

export const PRICE_CHOOSER_ID = 'pdp-single-cta-price-selector';

const PriceChooser = ({
  memberPrice,
  promoPrice,
  guestPrice,
  renderPromoHeader,
  showPromoText,
  shouldGreyedOut,
}: PriceChooserProps) => {
  const containerRef = useRef<any>(null);
  const [errorVisible, setErrorVisible] = useState(false);
  const {
    singleCTA: {
      priceOptionSelected,
      setPriceOptionSelected,
      priceSelectorErrorCounter,
    },
  } = useProductDetailContext();
  const { showMessage: showToastMessage } = useContext(ToastContext.Context);

  const handleChange = (value: string) => {
    setErrorVisible(false);
    setPriceOptionSelected(value);
  };

  useEffect(() => {
    if (priceSelectorErrorCounter === 0 || !containerRef.current) {
      return;
    }

    containerRef.current.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });

    setErrorVisible(true);
    showToastMessage(
      // @ts-ignore
      <FormattedMessage
        id="site_pdp.price_chooser_please_select_price"
        defaultMessage="Please, Select a Price"
      />
    );
  }, [priceSelectorErrorCounter]);

  const renderPriceOption = (type: 'member' | 'guest', ref) => {
    const price =
      type === 'member'
        ? showPromoText
          ? memberPrice
          : promoPrice
        : guestPrice;
    const isMember = type === 'member';
    const isSelected = priceOptionSelected === type;

    return (
      <RadioButtonWrapper
        data-autotag={isMember ? 'member-radio' : 'guest-radio'}
        isSelected={isSelected}
        isMember={isMember}
      >
        <RadioButtonContentWrapper
          ref={ref}
          isMember={isMember}
          showPromoText={showPromoText}
        >
          <StyledRadioButton
            data-autotag={
              isMember ? 'member-radio-button' : 'guest-radio-button'
            }
            value={type}
            isMember={isMember}
          />
          <RadioButtonContent isMember={isMember}>
            {isMember && showPromoText && (
              <MemberHeader>{renderPromoHeader}</MemberHeader>
            )}
            <PriceWrapper shouldGreyedOut={isMember && shouldGreyedOut}>
              {isMember ? (
                <FormattedMessage
                  id="site_pdp.member_new_price_label"
                  defaultMessage="Members:"
                />
              ) : (
                <FormattedMessage
                  id="site_pdp.guest_new_price_label"
                  defaultMessage="Guests:"
                />
              )}
              <PriceLabel shouldGreyedOut={isMember && shouldGreyedOut}>
                <Price amount={price === 0 ? memberPrice : price} />
              </PriceLabel>
            </PriceWrapper>
          </RadioButtonContent>
        </RadioButtonContentWrapper>
        {isMember && <FeaturedItemsList isVisible={isSelected} />}
      </RadioButtonWrapper>
    );
  };

  return (
    <PriceRadioGroup
      id={PRICE_CHOOSER_ID}
      value={priceOptionSelected}
      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
        handleChange(event.target.value)
      }
    >
      {errorVisible && (
        <ErrorTransparentBox>
          <InfoCircleIcon />
          <FormattedMessage
            id="site_pdp.price_chooser_price_selection_required"
            defaultMessage="Price Selection Required"
          />
        </ErrorTransparentBox>
      )}
      {renderPriceOption('member', containerRef)}
      {renderPriceOption('guest', null)}
    </PriceRadioGroup>
  );
};

export default PriceChooser;
